import axios from 'axios';

const API_BASE_URL = 'https://api.nexcard.co.in';
const ACCESS_TOKEN_COOKIE = 'accessToken';
const REFRESH_TOKEN_COOKIE = 'refreshToken';
const COOKIE_EXPIRY_DAYS = 7; // Access token validity
const HAS_LOGGED_IN_KEY = 'hasLoggedInBefore';
const OFFLINE_MODE_KEY = 'offlineMode';

// Keys used to cache last-known-good data for offline reads
export const OFFLINE_CACHE_KEYS = {
  holder: 'offline_holder_cards',
  webHolder: 'offline_web_holder_cards',
  myCards: 'offline_my_cards',
  cardDetailPrefix: 'offline_card_detail_', // suffix with card id
};

/**
 * Cookie utility functions
 * Note: HttpOnly cookies cannot be set from JavaScript for security reasons.
 * This implementation uses Secure cookies. For true HttpOnly cookies, 
 * backend must set cookies via Set-Cookie headers.
 */
const setCookie = (name, value, days) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  
  // Use Secure flag in production (HTTPS required)
  const secureFlag = window.location.protocol === 'https:' ? '; Secure' : '';
  const sameSiteFlag = '; SameSite=Strict';
  
  document.cookie = `${name}=${value}; expires=${expires.toUTCString()}; path=/${secureFlag}${sameSiteFlag}`;
};

const getCookie = (name) => {
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

const deleteCookie = (name) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

const setHasLoggedInBefore = () => {
  localStorage.setItem(HAS_LOGGED_IN_KEY, 'true');
};

export const hasLoggedInBefore = () => {
  return localStorage.getItem(HAS_LOGGED_IN_KEY) === 'true';
};

export const setOfflineMode = (isOffline) => {
  localStorage.setItem(OFFLINE_MODE_KEY, isOffline ? 'true' : 'false');
};

export const isOfflineMode = () => {
  return localStorage.getItem(OFFLINE_MODE_KEY) === 'true';
};

/**
 * Persist JSON-serializable data for offline reads
 */
export const cacheData = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (err) {
    console.warn('Failed to cache data', key, err);
  }
};

export const getCachedData = (key, fallback = null) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

export const clearCachedData = () => {
  Object.values(OFFLINE_CACHE_KEYS).forEach((value) => {
    if (typeof value === 'string') {
      localStorage.removeItem(value);
    }
  });
  // Remove all cached card detail entries with the prefix
  Object.keys(localStorage)
    .filter((key) => key.startsWith(OFFLINE_CACHE_KEYS.cardDetailPrefix))
    .forEach((key) => localStorage.removeItem(key));
};

export const isOnline = () => (typeof navigator !== 'undefined' ? navigator.onLine : true);

/**
 * Decode JWT token to check expiration
 */
const decodeToken = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    return null;
  }
};

/**
 * Check if token is valid (not expired)
 */
const isTokenValid = (token) => {
  if (!token) return false;
  
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return false;
  
  // Check if token expires in more than 1 minute (buffer time)
  const currentTime = Math.floor(Date.now() / 1000);
  return decoded.exp > currentTime + 60;
};

/**
 * Store tokens in cookies
 */
export const setTokens = (accessToken, refreshToken) => {
  setCookie(ACCESS_TOKEN_COOKIE, accessToken, COOKIE_EXPIRY_DAYS);
  setCookie(REFRESH_TOKEN_COOKIE, refreshToken, 30); // Refresh token typically lasts longer
  setHasLoggedInBefore();
  setOfflineMode(false);
};

/**
 * Get access token from cookie
 */
export const getAccessToken = () => {
  return getCookie(ACCESS_TOKEN_COOKIE);
};

/**
 * Get refresh token from cookie
 */
export const getRefreshToken = () => {
  return getCookie(REFRESH_TOKEN_COOKIE);
};

/**
 * Clear all tokens (logout)
 * This clears all tokens, cached data, and the hasLoggedInBefore flag
 * so offline access is no longer possible
 */
export const clearTokens = () => {
  deleteCookie(ACCESS_TOKEN_COOKIE);
  deleteCookie(REFRESH_TOKEN_COOKIE);
  setOfflineMode(false);
  // Clear cached data first (explicit cache keys)
  clearCachedData();
  // Clear hasLoggedInBefore flag so offline mode is disabled
  localStorage.removeItem(HAS_LOGGED_IN_KEY);
  // Clear all sessionStorage for backward compatibility
  sessionStorage.clear();
  // Clear all localStorage to ensure complete logout
  // This removes any other auth-related data that might be stored
  localStorage.clear();
};

/**
 * Check if access token is valid
 */
export const isAccessTokenValid = () => {
  const token = getAccessToken();
  return isTokenValid(token);
};

/**
 * Refresh access token using refresh token
 */
export const refreshAccessToken = async () => {
  const refreshToken = getRefreshToken();
  
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/auth/jwt/refresh/`,
      { refresh: refreshToken }
    );

    if (response.data.access) {
      // Update access token in cookie
      setCookie(ACCESS_TOKEN_COOKIE, response.data.access, COOKIE_EXPIRY_DAYS);
      
      // If new refresh token is provided, update it too
      if (response.data.refresh) {
        setCookie(REFRESH_TOKEN_COOKIE, response.data.refresh, 30);
      }
      
      return response.data.access;
    }
    
    throw new Error('No access token in refresh response');
  } catch (error) {
    console.error('Token refresh failed:', error);
    // Clear tokens on refresh failure
    clearTokens();
    throw error;
  }
};

/**
 * Validate and refresh token if needed
 * Returns valid access token or null if authentication failed
 */
export const ensureValidToken = async () => {
  // Check if access token is valid
  if (isAccessTokenValid()) {
    return getAccessToken();
  }

  // If offline, do not attempt refresh
  if (!isOnline()) {
    return null;
  }

  // Try to refresh token
  try {
    const newAccessToken = await refreshAccessToken();
    return newAccessToken;
  } catch (error) {
    console.error('Failed to ensure valid token:', error);
    return null;
  }
};

/**
 * Check authentication status on app startup
 * Returns true if user is authenticated, false otherwise
 */
export const checkAuthentication = async () => {
  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();
  const online = isOnline();

  // Offline handling
  if (!online) {
    const hasLoggedIn = hasLoggedInBefore();
    setOfflineMode(hasLoggedIn);
    return { isAuthenticated: hasLoggedIn, offlineMode: hasLoggedIn };
  }

  // Back online, clear offline flag
  setOfflineMode(false);

  // No tokens at all
  if (!accessToken && !refreshToken) {
    // If user has never logged in, don't allow offline mode
    if (!hasLoggedInBefore()) {
      return { isAuthenticated: false, offlineMode: false };
    }
    // If they had tokens before but now don't, they need to login
    return { isAuthenticated: false, offlineMode: false };
  }

  // Access token is valid
  if (isAccessTokenValid()) {
    return { isAuthenticated: true, offlineMode: false };
  }

  // Try to refresh using refresh token
  try {
    await refreshAccessToken();
    return { isAuthenticated: true, offlineMode: false };
  } catch (error) {
    // Refresh failed, clear tokens and force login
    clearTokens();
    return { isAuthenticated: false, offlineMode: false };
  }
};

/**
 * Helper to expose hasLoggedIn flag for UI
 */
export const hasLoggedInBeforeOnce = () => hasLoggedInBefore();

